import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import "../css/DateSelector.css";
import moment from "moment/moment";

// Custom hook for calendar logic
const useCalendar = (initialDate) => {
  const [currentDate, setCurrentDate] = useState(initialDate);

  const { startOfMonth, endOfMonth, startDay, daysInMonth } = useMemo(() => {
    const start = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    );
    const end = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0
    );
    return {
      startOfMonth: start,
      endOfMonth: end,
      startDay: (start.getDay() + 6) % 7,
      daysInMonth: end.getDate(),
    };
  }, [currentDate]);

  const changeMonth = useCallback(
    (offset) => {
      setCurrentDate(
        new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1)
      );
    },
    [currentDate]
  );

  return {
    currentDate,
    startOfMonth,
    endOfMonth,
    startDay,
    daysInMonth,
    changeMonth,
  };
};

// Utility functions
const isSameDay = (d1, d2) => {
  return (
    d1 &&
    d2 &&
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

const formatDateToYYYYMMDD = (date) => {
  return date.toISOString().split("T")[0];
};

// Calendar Header Component
const CalendarHeader = React.memo(({ monthName, onPrevMonth, onNextMonth }) => (
  <div className="calendar-header">
    <button className="calendar-nav" onClick={onPrevMonth}>
      «
    </button>
    <div id="currentMonth">{monthName}</div>
    <button className="calendar-nav" onClick={onNextMonth}>
      »
    </button>
  </div>
));

// Calendar Grid Component
const CalendarGrid = React.memo(({ calendarCells }) => (
  <div className="calendar-grid">{calendarCells}</div>
));

// Quick Dates Component
const QuickDates = React.memo(({ onQuickDateSelect }) => (
  <div className="quick-dates">
    <button className="quick-date-btn" onClick={() => onQuickDateSelect(1)}>
      Tomorrow
    </button>
    <button className="quick-date-btn" onClick={() => onQuickDateSelect(7)}>
      Next Week
    </button>
    <button className="quick-date-btn" onClick={() => onQuickDateSelect(14)}>
      2 Weeks
    </button>
    <button className="quick-date-btn" onClick={() => onQuickDateSelect(30)}>
      Next Month
    </button>
  </div>
));

const DateSelector = ({ selectedDate, onSelectDate, onMonthChange, count }) => {
  const { currentDate, startDay, daysInMonth, changeMonth } = useCalendar(
    new Date()
  );

  const handleMonthChange = useCallback(
    (offset) => {
      const newDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + offset,
        1
      );
      changeMonth(offset);
      onMonthChange(newDate);
    },
    [currentDate, changeMonth, onMonthChange]
  );

  const handleQuickDate = useCallback(
    (days) => {
      const newDate = new Date();
      newDate.setDate(newDate.getDate() + days);
      onSelectDate(newDate);
    },
    [onSelectDate]
  );

  const calendarCells = useMemo(() => {
    const cells = [];

    // Add empty cells for days before the start of the month
    for (let i = 0; i < startDay; i++) {
      cells.push(<div key={`empty-${i}`} className="calendar-cell empty" />);
    }

    // Add cells for each day of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const dayDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        i
      );
      const isSelected = isSameDay(dayDate, selectedDate);
      const dayAppointments = count?.find((item) =>
        moment(dayDate).isSame(item.date, "day")
      );

      cells.push(
        <button
          key={i}
          className={`flex flex-col items-center justify-center calendar-cell ${
            isSelected ? "selected" : ""
          }`}
          onClick={() => onSelectDate(dayDate)}
        >
          <span className="day-number">{i}</span>
          {dayAppointments && (
            <span className="appointment-count">
              {dayAppointments.noOfAppointments}
            </span>
          )}
        </button>
      );
    }

    return cells;
  }, [currentDate, startDay, daysInMonth, selectedDate, onSelectDate, count]);

  const monthName = useMemo(
    () =>
      currentDate.toLocaleString("default", { month: "long", year: "numeric" }),
    [currentDate]
  );

  return (
    <div className="date-selector">
      <label className="date-selector-label">Select target date:</label>
      <div className="calendar-container">
        <CalendarHeader
          monthName={monthName}
          onPrevMonth={() => handleMonthChange(-1)}
          onNextMonth={() => handleMonthChange(1)}
        />

        <div className="calendar-days">
          {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((day) => (
            <div className="day-header" key={day}>
              {day}
            </div>
          ))}
        </div>

        <CalendarGrid calendarCells={calendarCells} />
      </div>

      <QuickDates onQuickDateSelect={handleQuickDate} />
    </div>
  );
};

DateSelector.propTypes = {
  selectedDate: PropTypes.instanceOf(Date),
  onSelectDate: PropTypes.func.isRequired,
  onMonthChange: PropTypes.func.isRequired,
  count: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      noOfAppointments: PropTypes.number.isRequired,
    })
  ),
};

CalendarHeader.propTypes = {
  monthName: PropTypes.string.isRequired,
  onPrevMonth: PropTypes.func.isRequired,
  onNextMonth: PropTypes.func.isRequired,
};

CalendarGrid.propTypes = {
  calendarCells: PropTypes.arrayOf(PropTypes.node).isRequired,
};

QuickDates.propTypes = {
  onQuickDateSelect: PropTypes.func.isRequired,
};

export default React.memo(DateSelector);
